/*
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Dashboard
 * (C) Copyright IBM Corp. 2019, 2021
  * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
  */

define([
	'underscore',
	'../../../lib/@waca/upgrades/UpgradeBase',
	'./utils/WidgetUpgradeUtils',
	'../../../features/dashboard/colors/api/impl/Colors'
], (_, UpgradeBase, WidgetUpgradeUtils, Colors) => {

	/**
	* Upgrade summary widgets to the new version based on the KPI widget
	**/

	class SummaryKPIUpgrade extends UpgradeBase {

		constructor() {
			super();
			this.VERSION = 1401;
		}

		/**
		 * Perform upgrade
		 *
		 * @param {object} spec - spec to perform upgrade on
		 *
		 * @return {Promise} Promise to be resolved when upgrade performed
		 */
		up(spec) {
			const dashboard = this.data.dashboardApi;
			const dashboardThemeFeature = dashboard.getFeature('DashboardTheme');
			const paletteFeature = dashboard.getFeature('Palette');
			this._colorsService = new Colors({ features:{ API: this.data.dashboardApi, DashboardTheme: dashboardThemeFeature, Palette: paletteFeature } });
			if (!WidgetUpgradeUtils.specHasWidgets(spec)) {
				return Promise.resolve(spec);
			}
			const themeName = spec.theme || 'defaultTheme';
			return this._colorsService.initializeColorsFeatureForUpgrade({ themeName }).then(() => {
				let widgetPromises = [];
				_.each(spec.widgets, model => {
					if(model && WidgetUpgradeUtils.isLiveWidgetModel(model) && model.visId === 'summary'){
						widgetPromises.push(this._summaryWidgetUpgrade(model, spec));
					}
				});
				return Promise.all(widgetPromises).then(() => spec);
			});
		}

		// Downgrades are not available in CA
		down(spec) {
			return Promise.resolve(spec);
		}

		_summaryWidgetUpgrade(model, spec) {
			//Upgrade the slot mapping. The slot has changed from values to actual.
			if(!_.isEmpty(model.slotmapping)) {
				model.slotmapping.slots[0].name = 'actual';
			}
			model.enableSlotDefinitionFormat = false;
			let propertyMap = {};
			if (model.properties) {
				model.properties = model.properties.filter((property) => {
					propertyMap[property.id] = property.value;
					return (property.id !== 'summaryValueColor' && property.id !== 'summaryLabelColor');
				});
			}
			let paletteId = propertyMap.colorPalette || this._colorsService.getDefaultPaletteName('ColorPalette');
			const colorIndex = propertyMap.valueColor || 0;
			return this._colorsService.getHexColorFromPalette({
				paletteId: paletteId,
				colorIndex: colorIndex,
				defaultIfNotFound: true,
				addMissingBaseColors: false,
				themeName: spec.theme
			}).then((paletteHexColor) => {
				if (!model.properties) {
					model.properties = [];
				}
				const color = paletteHexColor.toUpperCase();
				this._addCustomColor(spec,color);
				model.properties.push({
					id: 'elementColor',
					value: 'customColor' + color.substring(1)
				});
			});
		}

		_addCustomColor(spec, color) {
			if (!spec.properties) {
				spec.properties = {};
			}
			if (!spec.properties.customColors) {
				spec.properties['customColors'] = {
					'colors': [color]
				};
			} else {
				let colorList = spec.properties.customColors.colors;
				if (colorList.indexOf(color) === -1) {
					colorList.push(color);
				}
			}
		}
	}

	return new SummaryKPIUpgrade();
});
